import type { LucideIcon } from 'lucide-react';
import {
  ADMIN_WORKSPACES,
  ADMIN_WORKSPACE_BY_ID,
  isAdminWorkspaceId,
  type AdminClientModule,
  type AdminWorkspace,
  type AdminWorkspaceId,
  type ModuleRoute,
  type ModuleSection,
} from '@wit-support/ui-domain';

/**
 * Ключ выбора «куда приземлиться на `/`». Соседствует с
 * `wit-admin:sidebar-collapsed` из `app-shell.tsx` и хранит РОВНО это — не
 * «текущее пространство». Текущее выводится из маршрута (см.
 * {@link workspaceForPath}); отдельное состояние для него означало бы, что
 * открытая по ссылке `/conversations/…` показывает сайдбар чужого
 * пространства до первого клика по переключателю.
 */
export const WORKSPACE_STORAGE_KEY = 'wit-admin:workspace';

export function readStoredWorkspace(): AdminWorkspaceId | null {
  try {
    const raw = window.localStorage.getItem(WORKSPACE_STORAGE_KEY);
    return raw !== null && isAdminWorkspaceId(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredWorkspace(id: AdminWorkspaceId): void {
  try {
    window.localStorage.setItem(WORKSPACE_STORAGE_KEY, id);
  } catch {
    // Best-effort — недоступное хранилище не должно ломать навигацию.
  }
}

/** Модули, которые вообще имеет смысл показывать этому оператору. */
export function navModulesFor(
  modules: readonly AdminClientModule[],
  permissions: readonly string[],
): AdminClientModule[] {
  return modules.filter((m) => m.showInNav !== false && permissions.includes(m.permission));
}

/** Модули одного пространства, в порядке `order` (как в плоском сайдбаре). */
export function modulesInWorkspace(
  modules: readonly AdminClientModule[],
  workspace: AdminWorkspaceId,
): AdminClientModule[] {
  return modules
    .filter((m) => m.workspace === workspace)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

/**
 * Пространства, в которых у оператора есть хотя бы один доступный модуль.
 * Порядок — из реестра, а не из порядка модулей.
 */
export function availableWorkspaces(navModules: readonly AdminClientModule[]): AdminWorkspace[] {
  return ADMIN_WORKSPACES.filter((w) => navModules.some((m) => m.workspace === w.id));
}

/**
 * Пространство, которому принадлежит текущий маршрут.
 *
 * Считается по ПОЛНОМУ реестру модулей, а не по видимым в навигации: модуль с
 * `showInNav: false` (история идентификации) открывается по прямой ссылке из
 * рейла чата и обязан подсветить своё пространство.
 *
 * Совпадение — только по границе сегмента: `/payment` и `/payment/x` да,
 * `/paymentsomething` нет.
 */
export function workspaceForPath(
  modules: readonly AdminClientModule[],
  path: string,
): AdminWorkspaceId | null {
  const hit = modules.find((m) => path === `/${m.id}` || path.startsWith(`/${m.id}/`));
  return hit?.workspace ?? null;
}

/**
 * Итоговое активное пространство. Приоритет жёсткий: маршрут → сохранённый
 * выбор → первое доступное. `null` только если доступных нет вовсе.
 *
 * Маршрут выигрывает ДАЖЕ если его пространства нет в списке доступных: туда
 * оператор попал по прямой ссылке, и сайдбар обязан показать то пространство,
 * которое он сейчас видит, а не чужое.
 */
export function resolveActiveWorkspace({
  routeWorkspace,
  stored,
  available,
}: {
  routeWorkspace: AdminWorkspaceId | null;
  stored: AdminWorkspaceId | null;
  available: readonly AdminWorkspace[];
}): AdminWorkspaceId | null {
  if (routeWorkspace) return routeWorkspace;
  if (stored && available.some((w) => w.id === stored)) return stored;
  return available[0]?.id ?? null;
}

/** Абсолютный адрес подраздела модуля: база модуля + относительный путь. */
export function sectionHref(module: AdminClientModule, section: ModuleSection): string {
  const base = `/${module.id}`;
  return section.path === '/' ? base : `${base}${section.path}`;
}

/**
 * Активный подраздел — тот, чей адрес ДЛИННЕЕ всех совпавших с маршрутом.
 *
 * Совпадение — по границе сегмента, как и у {@link workspaceForPath}. Корневой
 * подраздел ('/') совпадает со всем внутри модуля, поэтому он же работает
 * запасным вариантом: карточка кейса `/chargebacks/<id>` не имеет своей
 * вкладки и подсвечивает «Кейсы» — кейс к ним и относится. Более длинный
 * `/chargebacks/accounts` выигрывает у корня, так что запасной вариант не
 * перехватывает чужие экраны.
 *
 * `null` — маршрут вне модуля (подразделы тогда не рендерятся вовсе).
 */
export function activeSectionHref(module: AdminClientModule, path: string): string | null {
  let best: string | null = null;
  for (const section of module.sections ?? []) {
    const href = sectionHref(module, section);
    if (path !== href && !path.startsWith(`${href}/`)) continue;
    if (best === null || href.length > best.length) best = href;
  }
  return best;
}

/**
 * Подразделы, которые шелл рисует ВКЛАДКАМИ навбара. Только у модуля в режиме
 * `'tabs'` (по умолчанию — поле не задано); в режиме `'sidebar'` подразделы
 * живут в боковом меню, и навбар остаётся без вкладок.
 */
export function headerTabSections(module: AdminClientModule | undefined): ModuleSection[] {
  if (!module || module.sectionsPlacement === 'sidebar') return [];
  return module.sections ?? [];
}

/** Пункт бокового меню (ссылка). `depth: 1` — подраздел внутри группы. */
export interface SidebarEntry {
  key: string;
  href: string;
  label: string;
  icon: LucideIcon;
  depth: 0 | 1;
  /**
   * `current` — открытая страница (подсветка + `aria-current`); `none` — нет.
   */
  state: 'current' | 'none';
}

/**
 * Сворачиваемый раздел бокового меню: модуль с подразделами в сайдбаре
 * (`sectionsPlacement: 'sidebar'`) в пространстве, где модулей больше одного.
 * Заголовок раздела — переключатель (не ссылка): корень модуля и так доступен
 * своим подразделом («Сводка», «Обзор»).
 */
export interface SidebarGroup {
  kind: 'group';
  /** Ключ в хранилище свёрнутости — `module.id`. */
  key: string;
  label: string;
  icon: LucideIcon;
  /** Внутри открыт один из подразделов (маршрут в модуле). */
  containsCurrent: boolean;
  /** Подразделы, `depth: 1`. */
  children: SidebarEntry[];
}

export type SidebarItem = { kind: 'item' } & SidebarEntry;
export type SidebarNode = SidebarItem | SidebarGroup;

function inModule(module: AdminClientModule, path: string): boolean {
  const base = `/${module.id}`;
  return path === base || path.startsWith(`${base}/`);
}

function sectionItems(module: AdminClientModule, path: string, depth: 0 | 1): SidebarItem[] {
  const active = activeSectionHref(module, path);
  return (module.sections ?? []).map((s) => {
    const href = sectionHref(module, s);
    return {
      kind: 'item',
      key: `${module.id}:${s.path}`,
      href,
      label: s.label,
      icon: s.icon ?? module.icon,
      depth,
      state: href === active ? 'current' : 'none',
    };
  });
}

/**
 * Дерево бокового меню для списка модулей активного пространства.
 *
 * Группировку задают метаданные модулей, навигация ничего не знает о
 * конкретных разделах:
 * - модуль в режиме `'tabs'` (по умолчанию) или без подразделов — один пункт,
 *   активный по префиксу пути, как было всегда;
 * - модуль в режиме `'sidebar'`, единственный в списке, — подразделы плоско,
 *   сразу под названием пространства (иначе «Бухгалтерия» стояла бы дважды
 *   подряд: в переключателе и пунктом модуля);
 * - модуль в режиме `'sidebar'` рядом с другими модулями — сворачиваемая
 *   группа {@link SidebarGroup} с подразделами внутри.
 *
 * `moduleHref` переопределяет адрес пункта модуля (`null` — база модуля):
 * «Чаты» возвращают к последнему открытому диалогу. Группа ссылкой не
 * является, поэтому на неё не влияет.
 */
export function sidebarTree(
  modules: readonly AdminClientModule[],
  path: string,
  moduleHref: (m: AdminClientModule) => string | null = () => null,
): SidebarNode[] {
  const nodes: SidebarNode[] = [];
  const nested = modules.length > 1;
  for (const m of modules) {
    const inside = inModule(m, path);
    const hasSidebarSections = m.sectionsPlacement === 'sidebar' && (m.sections ?? []).length > 0;
    if (!hasSidebarSections) {
      nodes.push({
        kind: 'item',
        key: m.id,
        href: moduleHref(m) ?? `/${m.id}`,
        label: m.label,
        icon: m.icon,
        depth: 0,
        state: inside ? 'current' : 'none',
      });
      continue;
    }
    if (!nested) {
      nodes.push(...sectionItems(m, path, 0));
      continue;
    }
    nodes.push({
      kind: 'group',
      key: m.id,
      label: m.label,
      icon: m.icon,
      containsCurrent: inside,
      children: sectionItems(m, path, 1),
    });
  }
  return nodes;
}

/**
 * Маршрут модуля, который обслуживает путь. Сегмент `$param` совпадает с любым
 * непустым сегментом; при нескольких совпадениях выигрывает маршрут с меньшим
 * числом параметров (`/rules` у `/$caseId`), как в роутере.
 */
export function matchModuleRoute(module: AdminClientModule, path: string): ModuleRoute | null {
  if (!inModule(module, path)) return null;
  const rest = path
    .slice(module.id.length + 1)
    .split('/')
    .filter(Boolean);
  let best: { route: ModuleRoute; params: number } | null = null;
  for (const route of module.routes) {
    const segs = route.path.split('/').filter(Boolean);
    if (segs.length !== rest.length) continue;
    let params = 0;
    let ok = true;
    segs.forEach((seg, i) => {
      if (seg.startsWith('$')) params += 1;
      else if (seg !== rest[i]) ok = false;
    });
    if (ok && (best === null || params < best.params)) best = { route, params };
  }
  return best?.route ?? null;
}

export interface Breadcrumb {
  label: string;
  /** `null` — без ссылки (текущая страница или некуда вести). */
  href: string | null;
}

/** Подпись единственной крошки вне модулей (на `/`). */
export const HOME_CRUMB_LABEL = 'Главная';

/**
 * Хлебные крошки: пространство / модуль / подраздел / деталь.
 *
 * - Пространство ведёт на свой первый ДОСТУПНЫЙ модуль (как переключатель).
 * - Модуль пропускается, если его подпись совпадает с пространством
 *   («Контент / Контент» не нужен).
 * - Подраздел — только в режиме `'sidebar'`: во вкладочном режиме его и так
 *   называет активная вкладка рядом.
 * - Деталь — подпись, заданная страницей (`useBreadcrumbDetail`), иначе
 *   `ModuleRoute.label` маршрута, если это не сам подраздел (карточка
 *   `/$id` → «Публикация»).
 *
 * Последняя крошка — текущая страница, без ссылки.
 */
export function breadcrumbsFor({
  modules,
  navModules,
  path,
  detail,
}: {
  /** Полный реестр: и скрытый из навигации модуль получает свои крошки. */
  modules: readonly AdminClientModule[];
  /** Доступные оператору модули — для ссылки пространства. */
  navModules: readonly AdminClientModule[];
  path: string;
  detail: string | null;
}): Breadcrumb[] {
  const module = modules.find((m) => inModule(m, path));
  if (!module) return [{ label: HOME_CRUMB_LABEL, href: null }];

  const workspace = ADMIN_WORKSPACE_BY_ID[module.workspace];
  const firstInWorkspace = modulesInWorkspace(navModules, module.workspace)[0];
  const crumbs: Breadcrumb[] = [
    { label: workspace.label, href: firstInWorkspace ? `/${firstInWorkspace.id}` : null },
  ];
  const moduleBase = `/${module.id}`;
  if (module.label !== workspace.label) {
    crumbs.push({ label: module.label, href: moduleBase });
  }

  const sectionHrefs = new Set((module.sections ?? []).map((s) => sectionHref(module, s)));
  if (module.sectionsPlacement === 'sidebar') {
    const active = activeSectionHref(module, path);
    const section = (module.sections ?? []).find((s) => sectionHref(module, s) === active);
    if (section && active) crumbs.push({ label: section.label, href: active });
  }

  let detailLabel = detail;
  if (detailLabel === null) {
    const route = matchModuleRoute(module, path);
    const routeHref = route ? (route.path === '/' ? moduleBase : `${moduleBase}${route.path}`) : '';
    if (route?.label && !sectionHrefs.has(routeHref)) detailLabel = route.label;
  }
  if (detailLabel !== null && detailLabel !== crumbs[crumbs.length - 1]!.label) {
    crumbs.push({ label: detailLabel, href: null });
  }

  const last = crumbs[crumbs.length - 1]!;
  crumbs[crumbs.length - 1] = { ...last, href: null };
  return crumbs;
}

/**
 * Куда вести оператора с `/`: первый модуль сохранённого (если доступно) или
 * первого доступного пространства. `null` — доступных модулей нет.
 */
export function landingPath(
  navModules: readonly AdminClientModule[],
  stored: AdminWorkspaceId | null,
): string | null {
  const workspace = resolveActiveWorkspace({
    routeWorkspace: null,
    stored,
    available: availableWorkspaces(navModules),
  });
  if (!workspace) return null;
  const first = modulesInWorkspace(navModules, workspace)[0];
  return first ? `/${first.id}` : null;
}
